import { arrayMove } from "@dnd-kit/sortable";

import { rankBetween } from "@smart/itops-utils-basic";

import { fieldComponentDraggableIdPrefix } from "../constants";
import {
  checkIsSection,
  GqlField,
  GqlFieldValues,
  GqlGroupWithFieldValues,
  GqlSectionItem,
  isSectionItemField,
  isSectionItemGroup,
} from "../types";
import { isContactDetailsGroup } from "../utils";

export type DropResult = {
  activeId: string;
  overId: string | undefined;
  activeItem: GqlSectionItem;
  activeDisplayItem: GqlSectionItem;
  overItem: GqlSectionItem | undefined;
  delta: {
    x: number;
    y: number;
  };
};

export const checkIsFieldComponent = (id: string | undefined) =>
  id?.startsWith(fieldComponentDraggableIdPrefix);

export const getPositions = (
  items: { order: string }[],
  toPosition: number,
) => {
  const afterPosition =
    toPosition > 0 ? items[toPosition - 1]?.order : undefined;
  const beforePosition =
    toPosition < items.length ? items[toPosition]?.order : undefined;

  return { afterPosition, beforePosition };
};

export const getOrder = (
  destinationIndex: number,
  items: { order: string }[] | undefined | null,
) =>
  rankBetween({
    after: items?.[destinationIndex - 1]?.order,
    before: items?.[destinationIndex]?.order,
  });

export const isMappedField = (fieldValues: GqlFieldValues) =>
  !!fieldValues.layout && !!fieldValues.field;

export type GroupDropZone = "above" | "inside" | "below" | undefined;

/**
 * Check if the drop pointer is inside the group's field container area.
 * Returns 'above' if pointer is above the field container,
 * 'inside' if pointer is within the field container,
 * 'below' if pointer is below the field container,
 * or undefined if the group or container is not found.
 */
export const getDropZoneInGroup = (
  groupUri: string,
  dropPointerY: number,
): GroupDropZone => {
  const fieldContainer = document.querySelector(
    `[data-group-field-list="${groupUri}"]`,
  );
  if (!fieldContainer) return undefined;

  const rect = fieldContainer.getBoundingClientRect();

  if (dropPointerY < rect.top) return "above";
  if (dropPointerY > rect.bottom) return "below";
  return "inside";
};

export const flattenItems = (items: GqlSectionItem[]) =>
  items.flatMap((item) =>
    isSectionItemField(item) ? [item] : [item, ...item.fields],
  );

export const updateDisplayingSectionItemOnMovingInOrOutOfGroup = ({
  setSectionItemsToDisplay,
  activeId,
  overId,
  delta,
  dropPointerY,
}: {
  setSectionItemsToDisplay: (
    fn: (items: GqlSectionItem[]) => GqlSectionItem[],
  ) => void;
  activeId: string;
  overId: string | undefined;
  delta: DropResult["delta"];
  originalField: GqlField;
  dropPointerY?: number;
}) => {
  setSectionItemsToDisplay((prev) => {
    const flattenedItems = flattenItems(prev);
    const activeItem = flattenedItems.find((item) => item.uri === activeId);
    const overItem = flattenedItems.find((item) => item.uri === overId);

    if (!activeItem) return prev;

    const isOverItemGroup = overItem && isSectionItemGroup(overItem);
    const isOverEmptyGroup = isOverItemGroup && overItem.fields.length === 0;

    // Check if pointer is inside the group's field container area
    // If hovering over a group but outside the field container, don't trigger "move into group"
    const dropZone =
      isOverItemGroup && dropPointerY !== undefined
        ? getDropZoneInGroup(overItem.uri, dropPointerY)
        : undefined;
    const isGroupDropZoneInsideFieldContainer =
      !isOverItemGroup ||
      isOverEmptyGroup ||
      dropZone === "inside" ||
      dropZone === undefined;

    const getDestinationGroupUri = () => {
      if (isOverItemGroup && isGroupDropZoneInsideFieldContainer)
        return overItem.uri;
      if (overItem && isSectionItemField(overItem)) return overItem.groupUri;
      return undefined;
    };
    const destinationGroupUri = getDestinationGroupUri();

    const getGroupByUri = (uri: string | undefined | null) => {
      if (!uri) return undefined;
      return prev.find(
        (item) => isSectionItemGroup(item) && item.uri === uri,
      ) as GqlGroupWithFieldValues | undefined;
    };

    const sourceGroup = isSectionItemField(activeItem)
      ? getGroupByUri(activeItem.groupUri)
      : undefined;
    const destinationGroup = getGroupByUri(destinationGroupUri);
    const sourceIsContactDetails = isContactDetailsGroup(sourceGroup);
    const destinationIsContactDetails = isContactDetailsGroup(destinationGroup);

    const isMovingIntoGroup =
      isSectionItemField(activeItem) &&
      !activeItem.groupUri &&
      !!destinationGroupUri &&
      !destinationIsContactDetails;

    const isOverFieldAtSectionLevel =
      overItem && isSectionItemField(overItem) && !overItem.groupUri;

    // Check if hovering over a group but outside its field container (above/below)
    const isOverGroupButOutsideFieldContainer =
      isOverItemGroup && dropZone !== undefined && dropZone !== "inside";

    const isMovingOutOfGroup =
      isSectionItemField(activeItem) &&
      activeItem.groupUri &&
      !sourceIsContactDetails &&
      (!overItem ||
        isOverFieldAtSectionLevel ||
        isOverGroupButOutsideFieldContainer);

    const isMovingBetweenGroups =
      isSectionItemField(activeItem) &&
      activeItem.groupUri &&
      destinationGroupUri &&
      activeItem.groupUri !== destinationGroupUri &&
      !sourceIsContactDetails &&
      !destinationIsContactDetails;

    if (isMovingIntoGroup) {
      return prev
        .map((i) => {
          if (i.uri === activeId) return undefined;
          if (isSectionItemGroup(i) && i.uri === destinationGroupUri)
            return {
              ...i,
              fields: [
                ...i.fields,
                { ...activeItem, groupUri: destinationGroupUri },
              ],
            };
          return i;
        })
        .filter(Boolean) as GqlSectionItem[];
    }

    if (isMovingOutOfGroup) {
      const itemsWithActiveRemoved = prev.map((i) =>
        isSectionItemGroup(i) && i.uri === activeItem.groupUri
          ? { ...i, fields: i.fields.filter((f) => f.uri !== activeId) }
          : i,
      );

      const overIndex = itemsWithActiveRemoved.findIndex(
        (i) => i.uri === overId,
      );

      const getInsertionIndex = () => {
        if (overIndex >= 0) {
          const isDroppingBelow =
            dropZone === "below" || (dropZone === undefined && delta.y > 0);
          return isDroppingBelow ? overIndex + 1 : overIndex;
        }
        // To handle the case where the group is the first or last item in the section
        return delta.y > 0 ? itemsWithActiveRemoved.length : 0;
      };
      const insertionIndex = getInsertionIndex();

      return [
        ...itemsWithActiveRemoved.slice(0, insertionIndex),
        { ...activeItem, groupUri: undefined },
        ...itemsWithActiveRemoved.slice(insertionIndex),
      ];
    }

    if (isMovingBetweenGroups) {
      return prev.map((item) => {
        // Remove from source group
        if (isSectionItemGroup(item) && item.uri === activeItem.groupUri) {
          return {
            ...item,
            fields: item.fields.filter((f) => f.uri !== activeId),
          };
        }
        // Add to destination group
        if (isSectionItemGroup(item) && item.uri === destinationGroupUri) {
          return {
            ...item,
            fields: [
              ...item.fields,
              { ...activeItem, groupUri: destinationGroupUri },
            ],
          };
        }
        return item;
      });
    }

    return prev;
  });
};

export const updateDisplayingSectionItemOnDragEnd = ({
  setSectionItemsToDisplay,
  activeId,
  overId,
  flattenedDisplayItems,
  activeDisplayItem,
}: {
  setSectionItemsToDisplay: (
    fn: (items: GqlSectionItem[]) => GqlSectionItem[],
  ) => void;
  activeId: string;
  overId: string | undefined;
  flattenedDisplayItems: GqlSectionItem[];
  activeDisplayItem: GqlSectionItem | undefined;
}) => {
  setSectionItemsToDisplay((prev) => {
    const activeIndex = prev.findIndex((item) => item.uri === activeId);
    const overIndex = prev.findIndex((item) => item.uri === overId);

    if (activeIndex >= 0 && overIndex >= 0 && activeIndex !== overIndex) {
      return arrayMove(prev, activeIndex, overIndex >= 0 ? overIndex : 0);
    }

    const activeDisplayItemIndex = flattenedDisplayItems.findIndex(
      (item) => item.uri === activeId,
    );
    const overDisplayItemIndex = flattenedDisplayItems.findIndex(
      (item) => item.uri === overId,
    );

    const overDisplayItem = flattenedDisplayItems[overDisplayItemIndex];
    const activeInGroup =
      activeDisplayItem &&
      isSectionItemField(activeDisplayItem) &&
      activeDisplayItem.groupUri;
    const overInGroup =
      overDisplayItem &&
      isSectionItemField(overDisplayItem) &&
      overDisplayItem.groupUri;

    const isSameGroup =
      activeInGroup &&
      overInGroup &&
      activeDisplayItem.groupUri === overDisplayItem.groupUri;

    // Handle reordering within the same group
    if (isSameGroup && activeDisplayItemIndex !== overDisplayItemIndex) {
      return prev.map((item) => {
        if (item.uri !== activeDisplayItem.groupUri) return item;

        const groupFields = (item as GqlGroupWithFieldValues).fields;
        return {
          ...item,
          fields: arrayMove(
            groupFields,
            groupFields.findIndex((f) => f.uri === activeDisplayItem.uri),
            groupFields.findIndex((f) => f.uri === overDisplayItem.uri),
          ),
        };
      });
    }

    // Handle moving between different groups
    const isDifferentGroup =
      activeInGroup &&
      overInGroup &&
      activeDisplayItem.groupUri !== overDisplayItem.groupUri;

    if (isDifferentGroup) {
      return prev.map((item) => {
        if (isSectionItemGroup(item) && item.uri === overDisplayItem.groupUri) {
          const groupFields = item.fields;
          const activeFieldIndex = groupFields.findIndex(
            (f) => f.uri === activeDisplayItem.uri,
          );
          const overFieldIndex = groupFields.findIndex(
            (f) => f.uri === overDisplayItem.uri,
          );

          if (
            activeFieldIndex >= 0 &&
            overFieldIndex >= 0 &&
            activeFieldIndex !== overFieldIndex
          ) {
            return {
              ...item,
              fields: arrayMove(groupFields, activeFieldIndex, overFieldIndex),
            };
          }
        }
        return item;
      });
    }

    return prev;
  });
};

export const getActionType = ({
  overId: overIdFromDropResult,
  activeItem,
  overItem,
  activeDisplayItem,
}: DropResult) => {
  if (checkIsSection(overIdFromDropResult)) return "MOVE_ITEM_TO_SECTION";

  if (
    isSectionItemField(activeItem) &&
    isSectionItemField(activeDisplayItem) &&
    !activeItem.groupUri &&
    activeDisplayItem.groupUri
  )
    return "MOVE_FIELD_INTO_GROUP";

  if (
    isSectionItemField(activeItem) &&
    isSectionItemField(activeDisplayItem) &&
    activeItem.groupUri &&
    !activeDisplayItem.groupUri
  )
    return "MOVE_FIELD_OUT_OF_GROUP";

  if (
    isSectionItemField(activeItem) &&
    isSectionItemField(activeDisplayItem) &&
    activeItem.groupUri &&
    activeDisplayItem.groupUri &&
    activeItem.groupUri !== activeDisplayItem.groupUri
  )
    return "MOVE_FIELD_BETWEEN_GROUPS";

  if (
    isSectionItemField(activeItem) &&
    activeItem.groupUri &&
    overItem &&
    isSectionItemField(overItem) &&
    overItem.groupUri &&
    activeItem.groupUri === overItem.groupUri
  )
    return "CHANGE_GROUP_FIELD_ORDER";

  // Still visually inside a group (e.g. contactDetails move-out blocked) — do not
  // treat the drop as a section-level reorder.
  if (
    isSectionItemField(activeItem) &&
    activeItem.groupUri &&
    isSectionItemField(activeDisplayItem) &&
    activeDisplayItem.groupUri === activeItem.groupUri
  ) {
    return undefined;
  }

  if (isSectionItemField(activeItem) || isSectionItemGroup(activeItem))
    return "CHANGE_SECTION_ITEM_ORDER";

  return undefined;
};
